/**
 * "Changed outside Core" (decision 18): a person (or WhatConverts itself)
 * changed a lead's quotable mark, spam flag or value, and Core saw it at the
 * next import. The drawer lists each change; the leads table carries a small
 * badge. Core never reverts these, and says so.
 */
import { PencilLine } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import type { WcOutsideChange } from '@/services/whatconvertsService';

import { LABEL_TEXT } from './leadLabels';

const FIELD_TEXT: Record<string, string> = {
  quotable: 'Quotable',
  spam: 'Spam',
  quote_value: 'Quote value',
  sales_value: 'Sales value',
};

function valueText(field: string, value: string | null): string {
  if (value == null || value === '') return 'none';
  if (field === 'quotable') {
    return value === 'not_set' ? 'Not set' : (LABEL_TEXT as Record<string, string>)[value] ?? value;
  }
  if (field === 'spam') return value === 'true' ? 'Spam' : 'Not spam';
  return `$${value}`;
}

export function describeChange(c: WcOutsideChange): { field: string; from: string; to: string } {
  return {
    field: FIELD_TEXT[c.field] ?? c.field,
    from: valueText(c.field, c.old_value),
    to: valueText(c.field, c.new_value),
  };
}

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? iso
    : t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Small badge for the leads table; nothing when the lead was never changed outside Core. */
export function OutsideChangeBadge({ count }: { count: number | undefined }) {
  if (!count) return null;
  return (
    <Badge
      variant="outline"
      className="gap-1 border-amber/40 bg-amber-light px-1.5 py-0 text-[10px] text-amber-dark"
      data-testid="outside-change-badge"
      title={`${count} ${count === 1 ? 'change' : 'changes'} made outside Core. Open the lead to see them.`}
    >
      <PencilLine className="h-3 w-3" aria-hidden />
      Edited
    </Badge>
  );
}

/** The drawer list, newest first. Nothing when the list is empty. */
export function OutsideChangesList({ changes }: { changes: WcOutsideChange[] | undefined }) {
  if (!changes?.length) return null;
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="outside-changes">
      <div className="mb-1.5 text-sm font-medium">Changed outside Core</div>
      <ol className="space-y-1.5">
        {changes.map((c, i) => {
          const d = describeChange(c);
          return (
            <li key={`${c.seen_at}-${c.field}-${i}`} className="text-xs" data-testid={`outside-change-${i}`}>
              <span className="font-medium text-foreground">{d.field}</span>
              <span className="text-muted-foreground">
                {' '}
                {d.from} → {d.to}
              </span>
              <div className="text-muted-foreground">
                {c.wc_last_updated
                  ? `Changed in WhatConverts ${when(c.wc_last_updated)}${
                      c.wc_last_updated_naive ? ' (time zone unknown)' : ''
                    }, seen by Core ${when(c.seen_at)}`
                  : `Seen by Core ${when(c.seen_at)}`}
                {c.changed_by ? ` · by ${c.changed_by}` : ''}
              </div>
            </li>
          );
        })}
      </ol>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Core does not undo these. A Quotable mark that Core wrote and someone changed counts as a correction in the accuracy numbers.
      </p>
    </div>
  );
}
